import { createTheme } from '@mui/material/styles';
import { colors, semanticColors, palette } from './tokens/color';
import { radius } from './tokens/radius';
import { spacing } from './tokens/spacing';
import { elevation } from './tokens/elevation';
import { zIndex } from './tokens/zIndex';
import { getMuiTypography } from './tokens/typography/typography';

const defaultTheme = createTheme();

export const appTheme = createTheme({
  direction: 'rtl',
  palette: {
    primary: {
      main: semanticColors.brand.primary,
      light: palette.blue550,
      dark: palette.blue700,
      contrastText: semanticColors.text.inverse,
    },
    secondary: {
      main: semanticColors.brand.accent,
      light: palette.blue600,
      dark: palette.blue800,
      contrastText: semanticColors.text.inverse,
    },
    error: {
      main: semanticColors.state.error,
      light: semanticColors.state.errorLight,
    },
    warning: {
      main: semanticColors.state.warning,
    },
    success: {
      main: palette.green500,
      light: palette.green100,
    },
    background: {
      default: semanticColors.surface.screen,
      paper: semanticColors.surface.modal,
      lightBlue: semanticColors.surface.subtle,
      lightGray: semanticColors.surface.light,
    },
    text: {
      primary: semanticColors.text.primary,
      secondary: semanticColors.text.secondary,
      subtle: semanticColors.text.subtle,
      inverse: semanticColors.text.inverse,
      question: palette.gray900,
      answer: palette.blue700,
      inputLabel: palette.blue700,
      error: semanticColors.state.error,
    },
    border: {
      main: semanticColors.border.secondary,
      hover: palette.blue700,
      light: semanticColors.border.light,
      default: semanticColors.border.default,
    },
    button: {
      primary: semanticColors.brand.primary,
      gradient: {
        start: palette.gradientStart,
        end: palette.blue650,
      },
      hover: {
        start: palette.purple500,
        end: palette.blue700,
      },
    },
    icon: {
      expanded: semanticColors.brand.primary,
      collapsed: semanticColors.icon.subtle,
      primary: semanticColors.icon.primary,
      secondary: semanticColors.icon.secondary,
    },
    brands: {
      primary: palette.blue700,
      text: {
        active: palette.gray900,
        inactive: palette.blue550,
      },
      background: {
        default: palette.white,
        icon: palette.white,
      },
      shadow: {
        icon: '0px 4.62px 69.23px rgba(45, 85, 199, 0.08)',
        active: '0px 4px 20px 0px rgba(4, 104, 201, 0.16)',
      },
    },
    common: {
      white: palette.white,
      black: palette.black,
      shadow: {
        default: '0px 4.62px 69.23px rgba(45, 85, 199, 0.08)',
      },
    },
    serviceCard: {
      text: palette.gray900,
      background: palette.white,
    },
    savingsSection: {
      title: palette.blue700,
      subtitle: palette.blue550,
      amount: palette.blue700,
      slider: palette.blue400,
      sliderThumb: semanticColors.brand.primary,
      sliderRail: palette.blue800,
      sliderTrack: semanticColors.brand.primary,
      sliderTrackHover: palette.blue700,
    },
    doughnutChart: {
      primary: palette.blue700,
      secondary: palette.blue550,
      accent: palette.blue700,
      background: semanticColors.surface.subtle,
      text: palette.blue800,
      hover: palette.blue700,
    },
    benefitsSection: {
      title: palette.blue700,
      subtitle: palette.blue550,
      background: semanticColors.surface.subtle,
      text: palette.blue800,
      hover: palette.blue700,
    },
    formSection: {
      title: palette.blue800,
      subtitle: palette.blue700,
    },
    howToJoinSection: {
      cardContentHeader: palette.gray900,
      joinButton: semanticColors.brand.primary,
    },
    badge: semanticColors.badge,
    state: semanticColors.state,
  },
  typography: getMuiTypography(defaultTheme.breakpoints),
  shape: {
    borderRadius: radius.sm,
  },
  spacing: (factor) => `${spacing.sm * factor}px`,
  zIndex: {
    ...defaultTheme.zIndex,
    appBar: zIndex.sticky,
    drawer: zIndex.overlay,
    modal: zIndex.modal,
    snackbar: zIndex.toast,
    tooltip: zIndex.tooltip,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: radius.xxl,
          textTransform: 'none',
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: radius.lg,
          boxShadow: `0 ${elevation.low}px ${elevation.medium}px -1px rgba(0, 0, 0, 0.1), 0 ${elevation.low}px ${elevation.low * 2}px -1px rgba(0, 0, 0, 0.06)`,
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: radius.sm,
          },
        },
      },
    },
    MuiFormHelperText: {
      styleOverrides: {
        root: {
          textAlign: 'left',
          marginLeft: 14,
          marginRight: 0,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: radius.md,
        },
      },
    },
  },
});

export { colors, semanticColors, palette, radius, spacing, elevation, zIndex };

